import { useEffect, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProgressChecklist } from '../components/onboarding/ProgressChecklistContext';
import { send, listen } from 'loot-core/platform/client/fetch';
import { runQuery } from 'loot-core/client/query-helpers';
import { q } from 'loot-core/shared/query';

// Hook to automatically track progress based on user actions
export function useProgressTracking() {
  const { markTaskCompleted, state: checklistState } = useProgressChecklist();
  
  // Keep refs to avoid stale closures in window functions and callbacks
  const markTaskCompletedRef = useRef(markTaskCompleted);
  const tasksRef = useRef(checklistState.tasks);
  
  useEffect(() => {
    markTaskCompletedRef.current = markTaskCompleted;
  }, [markTaskCompleted]);
  
  useEffect(() => {
    tasksRef.current = checklistState.tasks;
  }, [checklistState.tasks]);
  
  // Helper function to mark task only if not already completed
  const markTaskIfNotCompleted = useCallback((taskId: string) => {
    const task = tasksRef.current.find(t => t.id === taskId);
    if (task && !task.completed) {
      markTaskCompletedRef.current(taskId);
    }
  }, []);
  
  // Get data from Redux store to track user actions
  const linkedAccounts = useSelector((state: any) => state.account?.linkedAccounts || []);
  const [transactions, setTransactions] = useState<any[]>([]);
  
  // Fetch transactions and listen for sync events
  const fetchTransactionsRef = useRef<() => void>(() => {});
  
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let unlistenSync: (() => void) | null = null;
    
    const fetchTransactions = async () => {
      try {
        const { data } = await runQuery(
          q('transactions')
            .select('*')
            .options({ splits: 'none' })
            .limit(1000) // Limit to avoid performance issues
        );
        if (data && Array.isArray(data)) {
          const reviewed = data.filter(t => t.reviewed === true).length;
          const unreviewed = data.filter(t => t.reviewed === false).length;
          setTransactions(data);
        }
      } catch (error) {
        // Ignore errors
        console.warn('Failed to fetch transactions for progress tracking:', error);
      }
    };
    
    // Store fetch function in ref for external access
    fetchTransactionsRef.current = fetchTransactions;
    
    // Fetch immediately
    fetchTransactions();
    
    // Listen to sync events to update transactions when they change
    unlistenSync = listen('sync-event', ({ type, tables }) => {
      console.log('Sync event received for progress tracking:', { type, tables });
      if (type === 'applied' && (tables.includes('transactions') || tables.includes('category_mapping'))) {
        fetchTransactions();
      }
    });
    
    // Also fetch periodically (every 3 seconds) as a fallback - reduced from 10s for faster feedback
    intervalId = setInterval(fetchTransactions, 3000);
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (unlistenSync) {
        unlistenSync();
      }
    };
  }, []);
  
  // Set up global functions for manual tracking (since some actions aren't in Redux)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).markRuleCreated = () => {
        const task = tasksRef.current.find(t => t.id === 'create-rule');
        if (task && !task.completed) {
          markTaskCompletedRef.current('create-rule');
        }
      };
      (window as any).markBatchActionCompleted = () => {
        const task = tasksRef.current.find(t => t.id === 'batch-action');
        if (task && !task.completed) {
          markTaskCompletedRef.current('batch-action');
        }
      };
      // Manual refresh trigger for progress tracking (used after batch operations)
      (window as any).refreshProgressTracking = () => {
        console.log('Manual refreshProgressTracking called');
        fetchTransactionsRef.current();
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).markRuleCreated;
        delete (window as any).markBatchActionCompleted;
        delete (window as any).refreshProgressTracking;
      }
    };
  }, []);
  
  // Track account linking - check for linked accounts specifically
  useEffect(() => {
    if (linkedAccounts.length > 0) {
      markTaskIfNotCompleted('link-account');
    }
  }, [linkedAccounts.length, markTaskIfNotCompleted]);

  // Track transaction categorization
  // We need to track only NEW categorizations made during onboarding, not existing categorized transactions
  const initialCategorizedCountRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (transactions.length === 0) return;
    
    // Filter out parent transactions (splits) and check for categorized transactions
    const categorizedTransactions = transactions.filter((transaction: any) => 
      !transaction.is_parent && // Exclude parent transactions
      transaction.category && 
      transaction.category !== null &&
      transaction.category !== '' &&
      transaction.category !== undefined
    );
    
    // Store initial count on first run
    if (initialCategorizedCountRef.current === null) {
      initialCategorizedCountRef.current = categorizedTransactions.length;
    }
    
    // Calculate new categorizations made during this session
    const newCategorizations = categorizedTransactions.length - (initialCategorizedCountRef.current || 0);
    
    if (newCategorizations >= 5) {
      markTaskIfNotCompleted('categorize-5-transactions');
    }
  }, [transactions, markTaskIfNotCompleted]);

  // Track transaction review (transactions with notes or manually edited)
  // We need to track only NEW reviews made during onboarding, not existing reviewed transactions
  const initialReviewedCountRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (transactions.length === 0) return;
    
    // Filter out parent transactions and check for reviewed transactions
    const reviewedTransactions = transactions.filter((transaction: any) => {
      if (transaction.is_parent) return false; // Exclude parent transactions
      
      // Check if the transaction has been explicitly marked as reviewed
      return transaction.reviewed === true;
    });
    
    // Store initial count on first run
    if (initialReviewedCountRef.current === null) {
      initialReviewedCountRef.current = reviewedTransactions.length;
    }
    
    // Calculate new reviews made during this session
    const newReviews = reviewedTransactions.length - (initialReviewedCountRef.current || 0);
    
    if (newReviews >= 5) {
      markTaskIfNotCompleted('review-5-transactions');
    }
  }, [transactions, markTaskIfNotCompleted]);

  // Track rule creation - check for existing rules on mount and track new ones via window.markRuleCreated
  const hasCheckedRulesRef = useRef(false);
  useEffect(() => {
    // Check if rules already exist (only once on mount)
    if (!hasCheckedRulesRef.current) {
      hasCheckedRulesRef.current = true;
      send('rules-get').then((rules: any) => {
        if (rules && rules.length > 0) {
          markTaskIfNotCompleted('create-rule');
        }
      }).catch(() => {
        // Ignore errors
      });
    }
  }, [markTaskIfNotCompleted]);
  
  // Note: New rule creation is tracked manually via window.markRuleCreated
  // (set up in the useEffect above) since rules aren't stored in Redux

  // Track budget creation - check if budget amounts have been set (not just budget name)
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let unlistenSync: (() => void) | null = null;
    
    const checkBudget = async () => {
      try {
        // Check if any budget amounts have been set
        // First try checking budget template lines (annual budgets)
        try {
          const { data: templateLines } = await runQuery(
            q('budget_template_lines')
              .filter({ amount: { $ne: 0 } })
              .select('id')
              .limit(1)
          );
          
          if (templateLines && templateLines.length > 0) {
            markTaskIfNotCompleted('create-budget');
            return;
          }
        } catch (err) {
          // Template lines might not exist, continue to check regular budgets
        }
        
        // Check zero_budgets or reflect_budgets
        try {
          const { data: zeroBudgets } = await runQuery(
            q('zero_budgets')
              .filter({ amount: { $ne: 0 } })
              .select('id')
              .limit(1)
          );
          
          if (zeroBudgets && zeroBudgets.length > 0) {
            markTaskIfNotCompleted('create-budget');
            return;
          }
        } catch (err) {
          // zero_budgets might not exist, try reflect_budgets
        }
        
        // Check reflect_budgets
        try {
          const { data: reflectBudgets } = await runQuery(
            q('reflect_budgets')
              .filter({ amount: { $ne: 0 } })
              .select('id')
              .limit(1)
          );
          
          if (reflectBudgets && reflectBudgets.length > 0) {
            markTaskIfNotCompleted('create-budget');
          }
        } catch (err) {
          // Ignore errors - budget might not be created yet
        }
      } catch (error) {
        // Ignore errors - budget might not be created yet
        console.warn('Failed to check budget creation:', error);
      }
    };
    
    // Check immediately
    checkBudget();
    
    // Listen to sync events to update when budgets change
    unlistenSync = listen('sync-event', ({ type, tables }) => {
      if (type === 'applied' && (
        tables.includes('zero_budgets') || 
        tables.includes('reflect_budgets') || 
        tables.includes('budget_template_lines')
      )) {
        checkBudget();
      }
    });
    
    // Also check periodically (every 10 seconds) as a fallback
    intervalId = setInterval(checkBudget, 10000);
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (unlistenSync) {
        unlistenSync();
      }
    };
  }, [markTaskIfNotCompleted]);

  // Track clearing of uncategorized and unreviewed transactions
  // We need to track initial state and only mark complete for NEW clears done during onboarding
  const initialUncategorizedCountRef = useRef<number | null>(null);
  const initialUnreviewedCountRef = useRef<number | null>(null);
  const hasLoggedInitialCount = useRef(false);
  
  useEffect(() => {
    // Only run if we have actual transactions loaded
    if (!transactions || transactions.length === 0) {
      return;
    }
    
    // Filter out parent transactions
    const nonParentTransactions = transactions.filter((t: any) => !t.is_parent);
    
    const uncategorizedTransactions = nonParentTransactions.filter((transaction: any) =>
      !transaction.category || transaction.category === null || transaction.category === ''
    );
    
    const unreviewedTransactions = nonParentTransactions.filter((transaction: any) =>
      transaction.reviewed === false
    );
    
    // Store initial counts on first run
    if (initialUncategorizedCountRef.current === null) {
      initialUncategorizedCountRef.current = uncategorizedTransactions.length;
    }
    if (initialUnreviewedCountRef.current === null) {
      initialUnreviewedCountRef.current = unreviewedTransactions.length;
    }
    
    // Clear uncategorized: ONLY complete if there WERE uncategorized at start AND now they're cleared
    if (initialUncategorizedCountRef.current > 0 && uncategorizedTransactions.length === 0) {
      markTaskIfNotCompleted('clear-uncategorized');
    }
    
    // Clear unreviewed: ONLY complete if there WERE unreviewed at start AND now they're cleared
    // Also mark review-5-transactions if 5+ were reviewed in this session
    if (initialUnreviewedCountRef.current > 0 && unreviewedTransactions.length === 0) {
      markTaskIfNotCompleted('clear-unreviewed');
      
      // Also mark review-5-transactions if cleared 5+ in the process
      if (initialUnreviewedCountRef.current >= 5) {
        markTaskIfNotCompleted('review-5-transactions');
      }
    }
  }, [transactions, markTaskIfNotCompleted]);

  // Note: Budget renaming is tracked manually in RenameBudgetModal component
  // Note: Batch actions are tracked manually in components that perform batch operations
  // See useManualProgressTracking hook for manual tracking methods

  return {
    // Expose methods for manual task completion
    markTaskCompleted,
  };
}

// Hook for manual task tracking (for actions that can't be automatically detected)
export function useManualProgressTracking() {
  const { markTaskCompleted } = useProgressChecklist();

  const markBatchActionCompleted = () => {
    markTaskCompleted('batch-action');
  };

  const markBudgetRenamed = () => {
    markTaskCompleted('rename-budget');
  };

  const markBackupExported = () => {
    markTaskCompleted('export-backup');
  };

  return {
    markBatchActionCompleted,
    markBudgetRenamed,
    markBackupExported,
  };
}
